import { LockScreen, useIdleLock } from "@ews-admin/global-design-system";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useActivityHeartbeat } from "../../hooks/useActivityHeartbeat";
import { usePinLockTexts } from "../../hooks/usePinLockTexts";
import { pinApi } from "../../services/pin.api";
import { sessionApi } from "../../services/session.api";
import {
  getSessionRequest,
  getSessionSuccess,
  lockSession,
  requestTerminateSessionRequest,
  selectForcedLogout,
  selectPinLockState,
  setPinStatus,
} from "../../store/reducers/sessionSlice";
import type { RootState } from "../../store/store";
import {
  PIN_LOCK_STORAGE_KEY,
  pinErrorMessage,
  pinLockTimeoutMs,
} from "../../utils/pin-utils";

/**
 * MED-1621 PIN lock of the dashboard session:
 * - no PIN yet (`hasPin === false`, or any 403 PIN_REQUIRED) → blocking "create" screen;
 * - server lock (`locked`, any 423 SESSION_LOCKED), idle for the user's delay (session
 *   `pinLockMinutes`, 15 minutes by default — MED-1643) or another tab locked →
 *   "unlock" screen, with the "forgot" flow (one-time code by SMS or email);
 * - `hasPin` absent (API older than the PIN lock) → the lock is off.
 * Five wrong PINs (401 PIN_ATTEMPTS_EXCEEDED) are handled by the API client and the session saga.
 */
export const PinLockProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { sessionStatus, hasPin, locked } = useSelector(selectPinLockState);
  const forcedLogout = useSelector(selectForcedLogout);
  // Also in the partial session sent while the session is locked or has no PIN.
  const user = useSelector((state: RootState) => state.session.session?.user);
  const pinLockMinutes = useSelector(
    (state: RootState) => state.session.session?.pinLockMinutes
  );
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ");
  const subtitle = fullName ? t("pinLock.signedInAs", { name: fullName }) : undefined;
  const active = sessionStatus === "VALID_SESSION" && typeof hasPin === "boolean";

  const [mode, setMode] = useState<"unlock" | "reset">("unlock");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [otpDescription, setOtpDescription] = useState<string>();
  const texts = usePinLockTexts(otpDescription);

  const onIdleLock = useCallback(() => {
    dispatch(lockSession());
  }, [dispatch]);
  const {
    locked: idleLocked,
    lock,
    unlock,
  } = useIdleLock({
    timeoutMs: pinLockTimeoutMs(pinLockMinutes),
    storageKey: PIN_LOCK_STORAGE_KEY,
    enabled: active && hasPin === true,
    onLock: onIdleLock,
  });

  // Keeps the server lock and the screen lock (shared with the other tabs) together.
  const previousIdleLocked = useRef(idleLocked);
  useEffect(() => {
    const wasIdleLocked = previousIdleLocked.current;
    previousIdleLocked.current = idleLocked;
    if (!active || hasPin !== true) return;
    if (wasIdleLocked && !idleLocked) {
      // Unlocked in another tab: the session is unlocked on the server too.
      if (locked) dispatch(setPinStatus({ hasPin: true, locked: false }));
      return;
    }
    if (locked && !idleLocked) lock();
  }, [active, hasPin, locked, idleLocked, lock, dispatch]);

  const showUnlock = active && hasPin === true && (locked || idleLocked);
  const showCreate = active && hasPin === false;

  // Keeps the server from locking an active user who makes no API call (reading, typing).
  useActivityHeartbeat(active && hasPin === true && !showUnlock);

  // A session locked when the app opens: the pages are only mounted once it is unlocked
  // (their calls would fail with 423). Once mounted they stay under the lock screen.
  const childrenShown = useRef(false);
  const hideChildren = showCreate || (showUnlock && !childrenShown.current);
  if (!hideChildren) childrenShown.current = true;

  // GET /session is partial while the session is locked or has no PIN: load the full session
  // before the pages are shown (they need the role, the facility, ...).
  const unlocked = async () => {
    try {
      dispatch(getSessionSuccess(await sessionApi.getSession()));
    } catch {
      dispatch(getSessionRequest());
    }
    dispatch(setPinStatus({ hasPin: true, locked: false }));
    unlock();
    setMode("unlock");
    setError(null);
    setOtpDescription(undefined);
  };

  // LockScreen expects the error text before the promise rejects.
  const failed = (err: unknown): never => {
    setError(pinErrorMessage(t, err));
    throw err;
  };

  const onCreate = async (pin: string) => {
    setError(null);
    try {
      await pinApi.create(pin);
    } catch (err) {
      if ((err as { code?: string })?.code === "PIN_ALREADY_SET") {
        dispatch(setPinStatus({ hasPin: true, locked: true }));
      }
      failed(err);
    }
    await unlocked();
  };

  const onUnlock = async (pin: string) => {
    setError(null);
    try {
      await pinApi.unlock(pin);
    } catch (err) {
      failed(err);
    }
    await unlocked();
  };

  const onForgot = async () => {
    setError(null);
    setBusy(true);
    try {
      const { channel, destinationMasked } = await pinApi.requestReset();
      setOtpDescription(
        t("pinLock.reset.sentTo", {
          channel: t(`pinLock.reset.channel.${channel}`),
          destination: destinationMasked,
        })
      );
      setMode("reset");
    } catch (err) {
      setError(pinErrorMessage(t, err));
    } finally {
      setBusy(false);
    }
  };

  const onReset = async (otp: string, newPin: string) => {
    setError(null);
    try {
      await pinApi.confirmReset(otp, newPin);
    } catch (err) {
      failed(err);
    }
    await unlocked();
  };

  // The session saga ends the session on the server, then opens the login page.
  const onSignOut = () => {
    dispatch(requestTerminateSessionRequest());
  };

  const onCancelReset = () => {
    setError(null);
    setMode("unlock");
  };

  if (forcedLogout) {
    return (
      <div
        role="alertdialog"
        aria-modal="true"
        data-testid="forced-logout"
        className="flex fixed inset-0 z-[1000] justify-center items-center p-6 bg-white"
      >
        <p className="max-w-sm text-center text-ews-gray-900">
          {t(`pinLock.forcedLogout.${forcedLogout}`)}
        </p>
      </div>
    );
  }

  return (
    <>
      {!hideChildren && children}
      {showCreate && (
        <LockScreen
          mode="create"
          texts={texts}
          subtitle={subtitle}
          error={error}
          onCreate={onCreate}
          onSignOut={onSignOut}
        />
      )}
      {showUnlock && mode === "unlock" && (
        <LockScreen
          mode="unlock"
          texts={texts}
          subtitle={subtitle}
          error={error}
          busy={busy}
          onUnlock={onUnlock}
          onForgot={onForgot}
          onSignOut={onSignOut}
        />
      )}
      {showUnlock && mode === "reset" && (
        <LockScreen
          mode="reset"
          texts={texts}
          subtitle={subtitle}
          error={error}
          onReset={onReset}
          onCancel={onCancelReset}
          onSignOut={onSignOut}
        />
      )}
    </>
  );
};
